import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useBestaetigung } from '@pilot-hub/ui'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, useNeuParam, FilterZurueck, KennzahlenLeiste, statusKennzahlen } from '../components/ListenWerkzeuge'
import { FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge, statusLabel } from '../components/StatusBadge'
import { PageHeader, Card, Button, Input, Select } from '@pilot-hub/ui'
import { api, akkuApi, droneApi } from '@pilot-hub/api-client'
import { FileText, ArrowLeft, Plus, Edit, Eye, Trash2, Save } from 'lucide-react'
import { AkkuDetail, ZustandBadge, ZyklenBalken, type AkkuHistorie, type Wartungsregeln } from '../components/AkkuDetail'
import { UnbekannteAkkus, type UnbekannterAkku } from '../components/UnbekannteAkkus'
import { DatumFeld } from '../components/DatumFelder'

interface Referenz { id: number; hersteller: string | null; modell: string | null }

interface AkkuTyp {
  id: number
  hersteller: string
  modell: string
  bezeichnung?: string | null
  kapazitat_mah?: number | null
  spannung_v?: number | null
  chemie?: string | null
  max_ladezyklen?: number | null
  wartung_zyklen?: number | null
  wartung_monate?: number | null
  hv_warn_tage?: number | null
  ladezeit_minuten?: number | null
  gewicht_gramm?: number | null
}

interface Drohne {
  id: number
  asb_inventarnummer: string
  hersteller: string | null
  modell: string | null
  dronen_referenz_id: number | null
}

interface Akku extends AkkuHistorie {
  id: number
  bezeichnung: string
  seriennummer: string | null
  dji_seriennummer: string | null
  dronen_referenz_id: number | null
  akku_typ_id: number | null
  hersteller: string | null
  modell: string | null
  kapazitat_mah: number | null
  spannung_v: number | null
  zellen: number | null
  status: string | null
  anzahl_ladezyklen: number | null
  letzte_wartung: string | null
  drohnen: Array<{ id: number; asb_inventarnummer: string }>
  drohne_ids: number[]
  besitzer_typ: string | null
  besitzer_nutzer_id: number | null
  besitzer_label: string | null
}

const STATUS_OPTIONEN = ['VERFUEGBAR', 'IM_EINSATZ', 'LADEN', 'WARTUNG', 'DEFEKT', 'AUSSER_BETRIEB']

const LEER = {
  bezeichnung: '', seriennummer: '', dji_seriennummer: '', hersteller: '', akku_typ_id: '',
  status: 'VERFUEGBAR', letzte_wartung: '', inbetriebnahme: '',
  besitzer_typ: 'ASB', besitzer_nutzer_id: ''
}

const fmt = (s: string | null) => (s ? new Date(s).toLocaleDateString('de-DE') : '—')

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; akkuId?: number }

export function AkkusPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const navigate = useNavigate()
  const [akkus, setAkkus] = useState<Akku[]>([])
  const [drohnen, setDrohnen] = useState<Drohne[]>([])
  const [referenzen, setReferenzen] = useState<Referenz[]>([])
  const [search, setSearch] = usePersistState('akkus.search', '')
  const [drohneFilter, setDrohneFilter] = usePersistState('akkus.drohneFilter', 'all')
  const [statusFilter, setStatusFilter] = usePersistState('akkus.statusFilter', 'all')
  const [zustandFilter, setZustandFilter] = usePersistState('akkus.zustandFilter', 'all')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [zuordnung, setZuordnung] = useState<number[]>([])
  const [speichert, setSpeichert] = useState(false)
  const [akkuTypen, setAkkuTypen] = useState<AkkuTyp[]>([])
  /** Nutzer, die als Besitzer eines Akkus gewählt werden dürfen (wie bei Drohnen) */
  const [besitzerNutzer, setBesitzerNutzer] = useState<{ id: number; vorname?: string | null; nachname?: string | null; benutzername: string }[]>([])
  const [nurHandlungsbedarf, setNurHandlungsbedarf] = usePersistState('akkus.nurHandlungsbedarf', false)
  /** Zähler zum Neuladen des Banners "unbekannte Akkus" */
  const [bannerStand, setBannerStand] = useState(0)

  const geaendert = useUngespeichert([form, zuordnung], JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [a, d, r, t] = await Promise.all([
        akkuApi.list({ size: 500 }),
        droneApi.list({ size: 500 }),
        droneApi.getReferenzen(),
        akkuApi.getTypes()
      ])
      setAkkus((a.data as any).items ?? [])
      setDrohnen((d.data as any).items ?? [])
      setReferenzen((r.data as any).items ?? [])
      const akkuData = (t?.data as any) ?? {};
      const parsedItems = akkuData.items ? akkuData.items : (Array.isArray(akkuData) ? akkuData : []);
      setAkkuTypen(parsedItems)
      setBannerStand(n => n + 1)
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Akkus konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])
  useNeuParam(oeffneNeu, !loading)
  useEffect(() => {
    api.get('/nutzer/besitzer-drohnen').then(r => setBesitzerNutzer(Array.isArray(r.data) ? (r.data as any) : [])).catch(() => {})
  }, [])

  // Direktlink /akkus?akku=<id> (z. B. aus dem Dashboard): Detailansicht öffnen, sobald die Liste geladen ist
  const [suchParameter, setSuchParameter] = useSearchParams()
  useEffect(() => {
    const id = Number(suchParameter.get('akku'))
    if (!id || loading) return
    const a = akkus.find(x => x.id === id)
    if (a) oeffneDetail(a)
    setSuchParameter({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suchParameter, loading, akkus])

  const herstellerListe = useMemo(
    () => Array.from(new Set(akkuTypen.map(t => t.hersteller).filter(Boolean))) as string[],
    [akkuTypen]
  )
  const modelleDesHerstellers = useMemo(
    () => akkuTypen.filter(t => !form.hersteller || t.hersteller === form.hersteller),
    [akkuTypen, form.hersteller]
  )

  // Es gibt keine Zuordnung Akku-Typ -> Drohnenmodell, daher sind alle Drohnen wählbar
  const passendeDrohnen = drohnen

  function toggleDrohne(id: number) {
    setZuordnung(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function oeffneNeu() {
    setForm({ ...LEER })
    setZuordnung([])
    setView({ mode: 'create' })
  }

  /** Aus dem Banner: Seriennummer und Inbetriebnahme (erster Flug) vorausfüllen. */
  function oeffneNeuAusUnbekannt(u: UnbekannterAkku) {
    setForm({ ...LEER, bezeichnung: `Akku ${u.seriennummer.slice(-4)}`, seriennummer: u.seriennummer,
              inbetriebnahme: u.erstmals ? u.erstmals.slice(0, 10) : '' })
    setZuordnung([])
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(a: Akku) {
    setForm({
      bezeichnung: a.bezeichnung ?? '',
      seriennummer: a.seriennummer ?? '',
      dji_seriennummer: a.dji_seriennummer ?? '',
      hersteller: a.hersteller ?? '',
      akku_typ_id: a.akku_typ_id ? String(a.akku_typ_id) : '',
      status: a.status ?? 'VERFUEGBAR',
      letzte_wartung: a.letzte_wartung ? a.letzte_wartung.slice(0, 10) : '',
      inbetriebnahme: a.inbetriebnahme ?? '',
      besitzer_typ: a.besitzer_typ || 'ASB',
      besitzer_nutzer_id: a.besitzer_nutzer_id ? String(a.besitzer_nutzer_id) : ''
    })
    setZuordnung(a.drohne_ids ?? [])
    setView({ mode: 'edit', akkuId: a.id })
  }

  function oeffneDetail(a: Akku) {
    setView({ mode: 'detail', akkuId: a.id })
    setForm({
      bezeichnung: a.bezeichnung,
      seriennummer: a.seriennummer || '',
      dji_seriennummer: a.dji_seriennummer || '',
      hersteller: a.hersteller || '',
      akku_typ_id: a.akku_typ_id ? String(a.akku_typ_id) : '',
      status: a.status || '',
      letzte_wartung: a.letzte_wartung ? a.letzte_wartung.slice(0, 10) : '',
      inbetriebnahme: a.inbetriebnahme ?? '',
      besitzer_typ: a.besitzer_typ || 'ASB',
      besitzer_nutzer_id: a.besitzer_nutzer_id ? String(a.besitzer_nutzer_id) : '',
    })
    setZuordnung(a.drohne_ids || [])
  }

  function zurueckZurListe() {
    setView({ mode: 'list' })
    setForm({ ...LEER })
    setZuordnung([])
  }

  async function speichern() {
    if (!form.bezeichnung) return
    if (form.besitzer_typ === 'NUTZER' && !form.besitzer_nutzer_id) { setFehler('Bitte einen Nutzer als Besitzer wählen'); return }
    setSpeichert(true); setFehler(null)
    try {
      const payload: any = {
        bezeichnung: form.bezeichnung,
        seriennummer: form.seriennummer || null,
        dji_seriennummer: form.dji_seriennummer || null,
        akku_typ_id: form.akku_typ_id ? Number(form.akku_typ_id) : null,
        status: form.status,
        letzte_wartung: form.letzte_wartung || null,
        inbetriebnahme: form.inbetriebnahme || null,
        besitzer_typ: form.besitzer_typ || 'ASB',
        besitzer_nutzer_id: form.besitzer_typ === 'NUTZER' && form.besitzer_nutzer_id ? Number(form.besitzer_nutzer_id) : null,
        drohne_ids: zuordnung
      }
      if (view.mode === 'edit' && view.akkuId) await akkuApi.update(view.akkuId, payload)
      else await akkuApi.create(payload)
      zurueckZurListe()
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await akkuApi.delete(id)
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen')
    }
  }

  const handlungsbedarf = akkus.filter(a => a.zustand && a.zustand !== 'gut').length
  const ZUSTAND_TEXT: Record<string, string> = { gut: 'Gut', wartung: 'Wartung fällig', pruefen: 'Prüfen' }
  const zustandListe = Array.from(new Set(akkus.map(a => a.zustand).filter((z): z is NonNullable<typeof z> => !!z)))
  const gefiltert = akkus.filter(a =>
    (drohneFilter === 'all' || (drohneFilter === 'keine' ? !(a.drohne_ids?.length) : (a.drohne_ids ?? []).includes(Number(drohneFilter)))) &&
    (statusFilter === 'all' || a.status === statusFilter) &&
    (zustandFilter === 'all' || a.zustand === zustandFilter) &&
    (!nurHandlungsbedarf || (a.zustand && a.zustand !== 'gut')) && (
    (a.bezeichnung ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (a.seriennummer ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (a.dji_seriennummer ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (a.modell ?? '').toLowerCase().includes(search.toLowerCase()))
  )

  const columns: SpalteDef[] = [
    { key: 'bez', header: 'Bezeichnung', accessor: 'bezeichnung', render: (_v: any, a: any) => <span className="font-medium">{a.bezeichnung || '—'}</span> },
    { key: 'modell', header: 'Modell', accessor: 'modell', sortWert: (a: any) => a.modell ? `${a.hersteller || ''} ${a.modell}` : null, render: (_v: any, a: any) => a.modell ? `${a.hersteller || ''} ${a.modell}`.trim() : '—' },
    { key: 'sn', header: 'Seriennummer', accessor: 'seriennummer', render: (_v: any, a: any) => (
      <span className="font-mono text-xs">
        {a.seriennummer || '—'}
        {a.dji_seriennummer && <span className="block text-dark-text-subtle" title="Von DJI gemeldete Seriennummer">DJI: {a.dji_seriennummer}</span>}
      </span>
    ) },
    { key: 'besitzer', header: 'Besitzer', accessor: 'besitzer_label', render: (_v: any, a: any) => <span className="text-dark-text">{a.besitzer_label || 'ASB-Drohnenstaffel'}</span> },
    { key: 'zustand', header: 'Zustand', accessor: 'zustand', sortWert: (a: any) => ({ pruefen: 0, wartung: 1, gut: 2 } as Record<string, number>)[a.zustand] ?? 3, render: (_v: any, a: any) => <ZustandBadge zustand={a.zustand} gruende={a.zustand_gruende ?? []} /> },
    { key: 'zyklen', header: 'Ladezyklen', accessor: 'anzahl_ladezyklen', mobile: 'meta', render: (_v: any, a: any) => <ZyklenBalken zyklen={a.anzahl_ladezyklen} max={a.max_ladezyklen} /> },
    { key: 'status', header: 'Status', accessor: 'status', sortWert: (a: any) => statusLabel(a.status), render: (_v: any, a: any) => <StatusBadge wert={a.status} /> },
    { key: 'wartung', header: 'Letzte Wartung', accessor: 'letzte_wartung', render: (_v: any, a: any) => fmt(a.letzte_wartung) },
    { key: 'drohnen', header: 'Zugeordnete Drohnen', accessor: 'drohnen', mobile: 'hidden', sortWert: (a: any) => (a.drohnen ?? []).map((d: any) => d.asb_inventarnummer).join(', ') || null, render: (_v: any, a: any) =>
      a.drohnen?.length ? <span className="text-xs">{a.drohnen.map((d: any) => d.asb_inventarnummer).join(', ')}</span> : <span className="text-dark-text-subtle text-xs">nicht zugeordnet</span>
    },
    {
      key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, row: any) => (
        <ZeilenAktionen name={String(row.bezeichnung || row.seriennummer)} onBearbeiten={() => oeffneBearbeiten(row)} eintraege={[{ label: 'Wartungsbuch exportieren', icon: <FileText className="h-4 w-4" />, onClick: () => navigate(`/export?art=akku&id=${row.id}`) }]} onLoeschen={async () => { const name = row.bezeichnung || row.seriennummer; if (await bestaetigen({ titel: 'Akku löschen?', text: 'Akku', objekt: String(name), hinweis: 'Die Zuordnung zu Drohnen wird entfernt. Das kann nicht rückgängig gemacht werden.' })) akkuApi.delete(row.id).then(laden) }} />
      )
    },
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          subtitle="Akkubestand — Modell aus der Basis-Datenbank, pro Seriennummer nur ein Akku"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neuer Akku</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <UnbekannteAkkus onAnlegen={oeffneNeuAusUnbekannt} aktualisieren={bannerStand} akkus={akkus} onZusammengefuehrt={laden} />

        <Card>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Input placeholder="Bezeichnung, Seriennr., Modell" value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-80" />
            </div>
            <FilterZurueck aktiv={!!search || nurHandlungsbedarf || drohneFilter !== 'all' || statusFilter !== 'all' || zustandFilter !== 'all'} onClick={() => { setSearch(''); setNurHandlungsbedarf(false); setDrohneFilter('all'); setStatusFilter('all'); setZustandFilter('all') }} />
            <div className="text-sm text-dark-text-muted whitespace-nowrap sm:ml-auto">{gefiltert.length} von {akkus.length}</div>
          </div>
          <div className="mb-4">
            <KennzahlenLeiste items={[
              ...statusKennzahlen(akkus.map(a => a.status), statusFilter, setStatusFilter, 'Alle Akkus'),
              { label: 'Handlungsbedarf', anzahl: handlungsbedarf, aktiv: nurHandlungsbedarf, punkt: 'bg-amber-400', onClick: () => setNurHandlungsbedarf(!nurHandlungsbedarf) },
            ]} />
          </div>
          <div className="mb-4 grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Select value={drohneFilter} onChange={e => setDrohneFilter(e.target.value)} placeholder="Drohne"
              options={[{ value: 'all', label: 'Alle Drohnen' }, { value: 'keine', label: 'Keiner Drohne zugeordnet' },
                ...drohnen.map(d => ({ value: String(d.id), label: d.asb_inventarnummer }))]} />
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} placeholder="Status"
              options={[{ value: 'all', label: 'Alle Status' }, ...STATUS_OPTIONEN.map(s => ({ value: s, label: statusLabel(s) }))]} />
            <Select value={zustandFilter} onChange={e => setZustandFilter(e.target.value)} placeholder="Zustand"
              options={[{ value: 'all', label: 'Alle Zustände' }, ...zustandListe.map(z => ({ value: z, label: ZUSTAND_TEXT[z] ?? z }))]} />
          </div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Akkus…</div> : (
            <ListenTabelle persistKey="akkus" data={gefiltert} columns={columns} keyAccessor={(a: any) => String(a.id)} onRowClick={oeffneDetail} emptyMessage="Keine Akkus vorhanden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW ======
  const currentAkku = view.akkuId ? akkus.find(a => a.id === view.akkuId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  const stammForm = (
      <Card>
        <div className="space-y-4">
          <div>
            <label className="label">Bezeichnung</label>
            <Input value={form.bezeichnung} onChange={e => setForm({...form, bezeichnung: e.target.value})} disabled={isDetail} placeholder="z.B. Akku 1" />
          </div>
          <div>
            <label className="label">Seriennummer</label>
            <Input value={form.seriennummer} onChange={e => setForm({...form, seriennummer: e.target.value})} disabled={isDetail} placeholder="z.B. DJI-12345" />
          </div>
          {(form.dji_seriennummer || isEdit) && (
            <div>
              <label className="label">DJI-Seriennummer (abweichend)</label>
              <Input value={form.dji_seriennummer} onChange={e => setForm({...form, dji_seriennummer: e.target.value})} disabled={isDetail} placeholder="nur falls DJI eine andere Nummer meldet" />
              <p className="mt-1 text-xs text-dark-text-subtle">Wird beim Zusammenführen eines unbekannten Akkus gesetzt. Flüge mit dieser Nummer werden diesem Akku zugeordnet.</p>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Besitzer</label>
              <Select value={form.besitzer_typ || 'ASB'} onChange={e => setForm({...form, besitzer_typ: e.target.value, besitzer_nutzer_id: ''})} disabled={isDetail}
                options={[{ value: 'ASB', label: 'ASB-Drohnenstaffel' }, { value: 'NUTZER', label: 'Privat (Nutzer)' }]} />
            </div>
            {form.besitzer_typ === 'NUTZER' && (
              <div>
                <label className="label">Nutzer</label>
                <Select value={form.besitzer_nutzer_id || ''} onChange={e => setForm({...form, besitzer_nutzer_id: e.target.value})} disabled={isDetail}
                  options={besitzerNutzer.map(n => ({ value: String(n.id), label: `${`${n.vorname || ''} ${n.nachname || ''}`.trim() || n.benutzername} (${n.benutzername})` }))}
                  placeholder="— Nutzer wählen —" />
              </div>
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Hersteller</label>
              <Select value={form.hersteller} onChange={e => setForm({...form, hersteller: e.target.value, akku_typ_id: ''})} disabled={isDetail}
                options={herstellerListe.map(h => ({ value: h, label: h }))}
                placeholder="— Hersteller wählen —" />
            </div>
            <div>
              <label className="label">Modell</label>
              <Select value={form.akku_typ_id} onChange={e => setForm({...form, akku_typ_id: e.target.value})} disabled={isDetail}
                options={modelleDesHerstellers.map(m => ({ value: String(m.id), label: [m.bezeichnung || m.modell, m.kapazitat_mah ? m.kapazitat_mah + ' mAh' : ''].filter(Boolean).join(' — ') }))}
                placeholder="— Modell wählen —" />
            </div>
          </div>
          {/* Info-Panel aus akku_typen (Flugzeit, Ladezeit, Kapazität, ...) */}
          <div>
            {form.akku_typ_id && (() => {
              const t = akkuTypen.find(x => String(x.id) === form.akku_typ_id)
              return t ? (
                <div className="rounded-lg bg-dark-bg-tertiary/60 p-3 text-xs space-y-0.5 border border-dark-border">
                  <p className="font-semibold text-dark-text mb-1">{t.hersteller} {t.bezeichnung || t.modell}</p>
                  {/* Je Zeile Label + Wert; 4 Spalten = 2 Paare nebeneinander */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-dark-text-muted">
                    <span>Kapazität:</span><span className="text-dark-text font-medium">{t.kapazitat_mah ? t.kapazitat_mah + ' mAh' : '—'}</span>
                    <span>Spannung:</span><span className="text-dark-text font-medium">{t.spannung_v ? Number(t.spannung_v).toLocaleString('de-DE') + ' V' : '—'}</span>
                    <span>Chemie:</span><span className="text-dark-text font-medium">{t.chemie || '—'}</span>
                    <span>Gewicht:</span><span className="text-dark-text font-medium">{t.gewicht_gramm ? t.gewicht_gramm + ' g' : '—'}</span>
                    <span>Ladezeit:</span><span className="text-dark-text font-medium">{t.ladezeit_minuten ? t.ladezeit_minuten + ' min' : '—'}</span>
                    <span>Max. Ladezyklen:</span><span className="text-dark-text font-medium">{t.max_ladezyklen ?? '—'}</span>
                  </div>
                </div>
              ) : null
            })()}
          </div>
          <div>
            <label className="label">Status</label>
            <Select value={form.status} onChange={e => setForm({...form, status: e.target.value})} disabled={isDetail}
              options={STATUS_OPTIONEN.map(s => ({ value: s, label: statusLabel(s) }))} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Inbetriebnahme</label>
              <DatumFeld  value={form.inbetriebnahme} onChange={v => setForm({...form, inbetriebnahme: v})} disabled={isDetail} />
              {!isDetail && <p className="mt-1 text-xs text-dark-text-subtle">Wird beim ersten DJI-Flug automatisch gesetzt, falls leer.</p>}
            </div>
            <div>
              <label className="label">Letzte Wartung</label>
              <DatumFeld  value={form.letzte_wartung} onChange={v => setForm({...form, letzte_wartung: v})} disabled={isDetail} />
            </div>
          </div>
          <div>
            <label className="label">Zugeordnete Drohnen</label>
            <div className="flex flex-wrap gap-2">
              {passendeDrohnen.map(d => (
                <label key={d.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-lg bg-dark-bg-tertiary cursor-pointer hover:bg-primary-600/10 transition-colors">
                  <input type="checkbox" checked={zuordnung.includes(d.id)} onChange={() => toggleDrohne(d.id)} className="w-4 h-4 text-primary-500 border-gray-600 rounded focus:ring-primary-500" />
                  <span className="text-sm">{d.asb_inventarnummer}</span>
                </label>
              ))}
            </div>
            {passendeDrohnen.length === 0 && <span className="text-sm text-dark-text-muted">Keine Drohnen angelegt</span>}
          </div>

        </div>
      </Card>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCreate ? 'Neuer Akku' : isEdit ? 'Akku bearbeiten' : 'Akku anzeigen'}
        subtitle={isCreate ? 'Neuen Akku anlegen' : currentAkku ? currentAkku.bezeichnung : ''}
        action={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentAkku && <Button variant="secondary" onClick={() => oeffneBearbeiten(currentAkku)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          </div>
        }
      />

      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

      {isDetail && currentAkku ? (
        <AkkuDetail akku={currentAkku} stammdaten={stammForm} onGeaendert={laden}
          regeln={(akkuTypen.find(t => t.id === currentAkku.akku_typ_id) as Wartungsregeln | undefined) ?? null} />
      ) : stammForm}
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={!form.bezeichnung} onSpeichern={speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}